import { useEffect, useState, useCallback } from 'react';

interface FormPersistenceOptions<T> {
  formId: string;
  enabled?: boolean;
  debounceMs?: number;
  expiryHours?: number;
}

interface StoredFormData<T> {
  data: T;
  timestamp: number;
}

/**
 * Hook to persist form state in sessionStorage with auto-save indicator
 * @param initialValues - Initial form values
 * @param options - Configuration options
 * @returns Form values, setValue function, clearForm function, and lastSaved timestamp
 */
export const useFormPersistence = <T extends Record<string, any>>(
  initialValues: T,
  options: FormPersistenceOptions<T>
) => {
  const { formId, enabled = true, debounceMs = 500, expiryHours = 24 } = options;
  const storageKey = `form_${formId}`;
  const expiryMs = expiryHours * 60 * 60 * 1000;

  const [values, setValues] = useState<T>(initialValues);
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Load saved data on mount
  useEffect(() => {
    if (!enabled) return;

    try {
      const savedData = sessionStorage.getItem(storageKey);
      if (savedData) {
        const parsed: StoredFormData<T> = JSON.parse(savedData);
        
        // Check if expired
        if (Date.now() - parsed.timestamp > expiryMs) {
          sessionStorage.removeItem(storageKey);
          return;
        }

        setValues(parsed.data);
        setLastSaved(parsed.timestamp);
      }
    } catch (error) {
      console.error('Failed to restore form data:', error);
    }
  }, [storageKey, enabled, expiryMs]);

  // Save form data
  const saveFormData = useCallback((data: T, immediate = false) => {
    if (!enabled) return;

    const performSave = () => {
      try {
        const storedData: StoredFormData<T> = {
          data,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(storageKey, JSON.stringify(storedData));
        setLastSaved(Date.now());
      } catch (error) {
        console.error('Failed to save form data:', error);
      }
    };

    if (immediate) {
      performSave();
    } else {
      // Clear existing timeout
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
      // Debounce saves
      const timeout = setTimeout(performSave, debounceMs);
      setSaveTimeout(timeout);
    }
  }, [enabled, storageKey, debounceMs, saveTimeout]);

  // Update values and trigger save
  const updateValues = useCallback((updates: Partial<T> | ((prev: T) => T), immediate = false) => {
    setValues(prev => {
      const newValues = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      saveFormData(newValues, immediate);
      return newValues;
    });
  }, [saveFormData]);

  // Clear saved form data
  const clearFormData = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
      setValues(initialValues);
      setLastSaved(null);
      if (saveTimeout) {
        clearTimeout(saveTimeout);
        setSaveTimeout(null);
      }
    } catch (error) {
      console.error('Failed to clear form data:', error);
    }
  }, [storageKey, initialValues, saveTimeout]);

  // Check if there's saved data
  const hasSavedData = useCallback(() => {
    try {
      const savedData = sessionStorage.getItem(storageKey);
      if (!savedData) return false;
      
      const parsed: StoredFormData<T> = JSON.parse(savedData);
      return Date.now() - parsed.timestamp <= expiryMs;
    } catch {
      return false;
    }
  }, [storageKey, expiryMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  return {
    values,
    updateValues,
    clearFormData,
    lastSaved,
    hasSavedData: hasSavedData(),
  };
};
